/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Video Component
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       1st June 2022

*******************************************************************************************/
import React from 'react';

const noop  = () => {};
const obj   = {};
const arr   = [];

/**
 * Video component to render a video player with customizable options.
 *
 * @param {Object} props - Component properties.
 * @param {Array} [props.sourcesIn=arr] - Array of video sources.
 * @param {number} [props.indexIn=0] - Initial index of the video to play.
 * @param {boolean} [props.random=true] - Whether to play videos in random order.
 * @param {Function} [props.onPlay=noop] - Callback function when video starts playing.
 * @param {Function} [props.onEnded=noop] - Callback function when video ends.
 * @param {Function} [props.onIndexChange=noop] - Callback function when video index changes.
 * @param {boolean} [props.loop=true] - Whether the video should loop.
 * @param {boolean} [props.autoPlay=true] - Whether the video should autoplay.
 * @param {boolean} [props.muted=true] - Whether the video should be muted.
 * @param {Object} [props.videoProps=obj] - Additional properties for the video element.
 * @returns {JSX.Element} The rendered Video component.
 */
export const Video = ({
    sources         : sourcesIn         = arr, 
    index           : indexIn           = 0, 
    random                              = true, 
    onPlay                              = noop, 
    onEnded         : handleEnded       = noop, 
    onIndexChange   : handleIndexChange = noop,
    loop                                = true,
    autoPlay                            = true,
    muted                               = true,
    videoProps                          = obj,
    ...props
}) => {
    
    if(!Array.isArray(sourcesIn))
        throw new Error('sources must be an array');
    
    const sources           = React.useMemo(() => sourcesIn, [sourcesIn]);

    const [index, setIndex] = React.useState(indexIn);
    const src               = React.useMemo(() => sources[index], [sources,index]);
    const handlePlay        = React.useCallback(() => onPlay(src),[onPlay, src]);

    React.useEffect(() => {
        if(!random)
            return;
        const rand = Math.random();
        setIndex(
            Math.floor(
                rand * (sources?.length || 0)
            )
        )
    }, [random, sources?.length]);

    // Set Random Index
    React.useEffect(() => {
        if(random) 
            return;
        if(Number.isFinite(indexIn) && indexIn >= 0 && indexIn < sources.length){
            setIndex(indexIn);
        }
    },[indexIn, random, sources.length])

    // On Index Change
    React.useEffect(() => {
        handleIndexChange(index);
    },[index, handleIndexChange])

    if(sources.length <= 0)
        return null;

    return (
        <video 
            preload     = "none"
            loop        = {loop} 
            autoPlay    = {autoPlay}
            muted       = {muted}
            {...props}
            {...videoProps}
            onPlay      = {handlePlay}
            onEnded     = {handleEnded}
            src         = {src} 
        />
    )
}

export default Video;